import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Button from '../components/Button';
import SongCard from '../components/SongCard';
import MoodboardSelector from '../components/MoodboardSelector';
import TipTapEditor from '../components/TipTapEditor';
import config from '../config';

const AlbumDetail = () => {
  const { albumId } = useParams();
  const navigate = useNavigate();
  const [album, setAlbum] = useState(null);
  const [albumSongs, setAlbumSongs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showNotesEditor, setShowNotesEditor] = useState(false);
  const [saveStatus, setSaveStatus] = useState('saved'); // 'saved', 'saving', 'error'
  const saveTimeoutRef = useRef(null);
  
  useEffect(() => {
    const fetchAlbumData = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        // Fetch album details
        const albumResponse = await axios.get(`${config.API_URL}/albums/${albumId}`);
        setAlbum(albumResponse.data);
        
        // Fetch songs for this album - directly query songs by albumId
        const songsResponse = await axios.get(`${config.API_URL}/songs?albumId=${albumId}`);
        const allSongs = songsResponse.data || [];
        
        // Make sure we're filtering correctly - some APIs might return all songs
        const songsInAlbum = allSongs.filter(song => song.albumId === albumId);
        setAlbumSongs(songsInAlbum);
      } catch (err) {
        console.error('Error fetching album data:', err);
        setError('Failed to load album data. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchAlbumData();
  }, [albumId]);
  
  const handleDeleteAlbum = async () => {
    // Check if album has songs
    if (albumSongs.length > 0) {
      const confirmMessage = `This album contains ${albumSongs.length} song${albumSongs.length > 1 ? 's' : ''}. 
Deleting this album will also delete all songs in it. 

Are you sure you want to delete "${album.title}"?`;
      if (!window.confirm(confirmMessage)) {
        return; // User canceled deletion
      }
    } else if (!window.confirm(`Are you sure you want to delete "${album.title}"?`)) {
      return; // User canceled deletion
    }
    
    try {
      // Show loading state
      setIsLoading(true);
      
      // First delete all songs associated with this album
      if (albumSongs.length > 0) {
        const deletePromises = albumSongs.map(song => 
          axios.delete(`${config.API_URL}/songs/${song.id}`)
            .catch(error => {
              console.error(`Error deleting song ${song.id}:`, error);
              // Continue with other deletions even if one fails
              return null;
            })
        );
        await Promise.all(deletePromises);
      }
      
      // Then delete the album
      await axios.delete(`${config.API_URL}/albums/${albumId}`);
      
      // Navigate back to dashboard
      navigate('/');
    } catch (error) {
      console.error('Error deleting album:', error);
      alert('Failed to delete album. Please try again.');
      setIsLoading(false);
    }
  };
  
  const handleSongDeleted = (songId) => {
    setAlbumSongs(prev => prev.filter(song => song.id !== songId));
  };

  // Debounced save function for autosave
  const debouncedSave = useCallback(() => {
    // Clear any existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    
    // Set saving status
    setSaveStatus('saving');
    
    // Set a new timeout
    saveTimeoutRef.current = setTimeout(async () => {
      try {
        await axios.put(`${config.API_URL}/albums/${albumId}`, {
          ...album,
          username: config.USERNAME
        });
        
        // Set saved status
        setSaveStatus('saved');
      } catch (err) {
        console.error('Error auto-saving album notes:', err);
        setSaveStatus('error');
      }
    }, 1500); // 1.5 second delay for autosave
  }, [album, albumId]);

  const handleNotesChange = (content) => {
    setAlbum(prev => ({
      ...prev,
      notes: content
    }));
    debouncedSave();
  };
  
  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[60vh]">
        <div className="animate-pulse flex flex-col items-center">
          <div className="w-24 h-24 bg-gradient-to-r from-purple-500 to-indigo-600 rounded-full flex items-center justify-center">
            <svg className="w-12 h-12 text-white animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </div>
          <p className="mt-4 text-lg font-medium text-gray-600">Loading album details...</p>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="text-center py-10">
        <div className="bg-red-50 border border-red-400 text-red-700 px-4 py-3 rounded-md inline-block">
          <p>{error}</p>
          <button 
            onClick={() => navigate('/')}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
          >
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }
  
  if (!album) {
    return (
      <div className="text-center py-10">
        <p className="text-lg text-gray-600">Album not found</p>
        <button 
          onClick={() => navigate('/')}
          className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 transition-colors"
        >
          Return to Dashboard
        </button>
      </div>
    );
  }
  
  return (
    <div>
      <div className="mb-6">
        <Link to="/" className="text-primary-600 hover:text-primary-800 flex items-center">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
          </svg>
          Back to Dashboard
        </Link>
      </div>
      
      <div className="bg-white rounded-lg shadow-md overflow-hidden mb-8">
        <div className="md:flex">
          <div className="md:w-1/3 h-64 md:h-auto">
            <img 
              src={album.coverArtUrl} 
              alt={album.title} 
              className="w-full h-full object-cover"
            />
          </div>
          <div className="p-6 md:w-2/3">
            <div className="flex justify-between items-start">
              <h1 className="text-3xl font-bold text-gray-800 mb-2">{album.title}</h1>
              <div className="flex space-x-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => alert('Edit album would open here')}
                >
                  Edit
                </Button>
                <Button 
                  variant="danger" 
                  size="sm"
                  onClick={handleDeleteAlbum}
                >
                  Delete
                </Button>
              </div>
            </div>
            <p className="text-gray-600 mb-4">{album.description}</p>
            
            {/* Album Notes Section */}
            <div className="mt-6">
              <div className="flex justify-between items-center mb-2">
                <h3 className="text-lg font-semibold text-gray-700">Album Notes</h3>
                <div className="flex items-center">
                  {saveStatus === 'saving' && (
                    <span className="text-yellow-600 text-xs mr-2 flex items-center">
                      <svg className="animate-spin -ml-1 mr-2 h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Saving...
                    </span>
                  )}
                  {saveStatus === 'saved' && (
                    <span className="text-green-600 text-xs mr-2 flex items-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      Saved
                    </span>
                  )}
                  {saveStatus === 'error' && (
                    <span className="text-red-600 text-xs mr-2 flex items-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                      Error Saving
                    </span>
                  )}
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setShowNotesEditor(!showNotesEditor)}
                  >
                    {showNotesEditor ? 'Hide Editor' : 'Edit Notes'}
                  </Button>
                </div>
              </div>
              
              {showNotesEditor ? (
                <div>
                  <div className="flex justify-end mb-2">
                    <Button
                      variant="primary"
                      size="sm"
                      className="flex items-center"
                      onClick={async () => {
                        try {
                          setSaveStatus('saving');
                          const response = await axios.post(`${config.API_URL}/ai/generate`, {
                            type: 'album_notes',
                            context: {
                              albumTitle: album.title,
                              albumDescription: album.description,
                              songCount: albumSongs.length,
                              songTitles: albumSongs.map(song => song.title)
                            }
                          });
                          
                          if (response.data && response.data.content) {
                            // Update the album with the AI-generated notes
                            const newNotes = album.notes ? album.notes + '\n\n' + response.data.content : response.data.content;
                            setAlbum(prev => ({
                              ...prev,
                              notes: newNotes
                            }));
                            debouncedSave();
                          }
                          setSaveStatus('saved');
                        } catch (error) {
                          console.error('Error generating AI content:', error);
                          setSaveStatus('error');
                          alert('Failed to generate AI content. Please try again.');
                        }
                      }}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                      Generate with AI
                    </Button>
                  </div>
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <TipTapEditor
                      content={album.notes || ''}
                      onChange={(content) => {
                        handleNotesChange(content);
                      }}
                      placeholder="Add notes, ideas, or thoughts about this album..."
                    />
                  </div>
                </div>
              ) : (
                <div 
                  className="prose max-w-none border border-gray-200 rounded-lg p-4 bg-white min-h-[100px] cursor-pointer hover:bg-gray-50"
                  onClick={() => setShowNotesEditor(true)}
                >
                  {album.notes ? (
                    <div dangerouslySetInnerHTML={{ __html: album.notes }} />
                  ) : (
                    <p className="text-gray-400 italic">No notes yet. Click to add notes about this album.</p>
                  )}
                </div>
              )}
            </div>
            <div className="flex items-center text-sm text-gray-500">
              <span>{albumSongs.length} songs</span>
              {album.createdAt && (
                <>
                  <span className="mx-2">&bull;</span>
                  <span>Created: {new Date(album.createdAt).toLocaleDateString()}</span>
                </>
              )}
              {album.updatedAt && album.updatedAt !== album.createdAt && (
                <>
                  <span className="mx-2">&bull;</span>
                  <span>Updated: {new Date(album.updatedAt).toLocaleDateString()}</span>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Moodboard Selector */}
      <MoodboardSelector 
        entityType="album" 
        entityId={albumId} 
        onSelect={(moodboardId) => console.log(`Selected moodboard: ${moodboardId}`)} 
      />
      
      <div className="mb-6 flex justify-between items-center">
        <h2 className="text-xl font-semibold text-gray-800">Songs</h2>
        <div className="flex space-x-2">
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => alert('Reorder songs would open here')}
          >
            Reorder Songs
          </Button>
          <Button 
            variant="primary" 
            size="sm"
            onClick={() => alert('Add song to album would open here')}
          >
            Add Song
          </Button>
        </div>
      </div>
      
      <div className="space-y-4">
        {albumSongs.length > 0 ? (
          albumSongs.map(song => (
            <SongCard key={song.id} song={song} onDelete={handleSongDeleted} />
          ))
        ) : (
          <div className="text-center py-10 bg-white rounded-lg shadow-sm border border-gray-200">
            <p className="text-gray-500">No songs in this album yet.</p>
            <Button 
              variant="primary" 
              className="mt-4"
              onClick={() => alert('Add song to album would open here')}
            >
              Add Your First Song
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AlbumDetail;
